import React, { useState, useEffect, useCallback, useRef } from "react";
import PromptInput from "./PromptInput";
import { motion, AnimatePresence } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Key, BookOpen, BarChart3, Code2, Globe, Shield,
  Copy, Plus, Trash2, RefreshCw, Eye, EyeOff,
  CheckCircle, AlertTriangle, Loader2, Play, ChevronRight,
  Activity, Clock, Zap, ExternalLink, Terminal,
  Webhook, Rocket, StopCircle, Users, Hash,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function copyToClipboard(text) {
  navigator.clipboard.writeText(text).then(() => toast.success("Copied!")).catch(() => toast.error("Copy failed"));
}

// ==================== API Keys Section ====================
function APIKeysSection() {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newKeyName, setNewKeyName] = useState("");
  const [creating, setCreating] = useState(false);
  const [revealedKey, setRevealedKey] = useState(null);

  const fetchKeys = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/keys`);
      setKeys(res.data.keys || []);
    } catch { /* non-blocking */ }
    setLoading(false);
  }, []);

  useEffect(() => { fetchKeys(); }, [fetchKeys]);

  const createKey = async () => {
    if (!newKeyName.trim()) { toast.error("Enter a key name"); return; }
    setCreating(true);
    try {
      const res = await axios.post(`${API}/keys/create`, { name: newKeyName.trim(), permissions: "full" });
      setRevealedKey(res.data.key);
      setNewKeyName("");
      toast.success("API key created! Copy it now — it won't be shown again.");
      fetchKeys();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create key");
    }
    setCreating(false);
  };

  const revokeKey = async (preview) => {
    try {
      await axios.post(`${API}/keys/revoke`, { key_preview: preview });
      toast.success("Key revoked");
      fetchKeys();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Revoke failed");
    }
  };

  return (
    <div className="space-y-4" data-testid="haven-api-keys-section">
      {/* Create New Key */}
      <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <Plus className="w-4 h-4 text-[var(--qn-primary)]" />
            Create API Key
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-3">
            <Input
              placeholder="Key name (e.g., production-server)"
              value={newKeyName}
              onChange={(e) => setNewKeyName(e.target.value)}
              className="bg-[var(--qn-surface)] border-[var(--qn-border)] flex-1"
              data-testid="haven-key-name-input"
              onKeyDown={(e) => e.key === "Enter" && createKey()}
            />
            <Button onClick={createKey} disabled={creating} className="bg-[var(--qn-primary)]" data-testid="haven-create-key-btn">
              {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Key className="w-4 h-4 mr-2" />Create</>}
            </Button>
          </div>
          {/* Reveal newly created key */}
          <AnimatePresence>
            {revealedKey && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="mt-3 p-3 rounded-lg border border-emerald-500/30 bg-emerald-500/5"
                data-testid="haven-revealed-key"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-emerald-400 font-medium mb-1">New API Key (copy now!)</p>
                    <code className="text-sm font-mono text-[var(--qn-foreground)] break-all">{revealedKey}</code>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => copyToClipboard(revealedKey)} className="shrink-0 ml-3">
                    <Copy className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </CardContent>
      </Card>

      {/* Existing Keys */}
      <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]">
        <CardHeader className="pb-3 flex flex-row items-center justify-between">
          <CardTitle className="text-sm font-semibold">Your API Keys</CardTitle>
          <Button variant="ghost" size="sm" onClick={fetchKeys} className="h-7 text-xs">
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" />Refresh
          </Button>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8 text-[var(--qn-foreground-muted)]">
              <Loader2 className="w-5 h-5 animate-spin mr-2" />Loading keys...
            </div>
          ) : keys.length === 0 ? (
            <p className="text-sm text-[var(--qn-foreground-muted)] py-4 text-center">No API keys yet. Create one above to get started.</p>
          ) : (
            <div className="space-y-2">
              {keys.map((k, i) => (
                <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-[var(--qn-surface)]/30 border border-[var(--qn-border)]" data-testid={`haven-key-${k.key_preview}`}>
                  <div className="flex items-center gap-3">
                    <Key className="w-4 h-4 text-[var(--qn-primary)]" />
                    <div>
                      <span className="text-sm font-medium text-[var(--qn-foreground)]">{k.name}</span>
                      <div className="text-xs font-mono text-[var(--qn-foreground-muted)]">{k.key_preview}...****</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-[10px]">{k.permissions || "full"}</Badge>
                    <Button variant="ghost" size="sm" onClick={() => revokeKey(k.key_preview)} className="h-7 text-red-400 hover:bg-red-500/10">
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ==================== Interactive Docs Section ====================
function InteractiveDocsSection() {
  const [docs, setDocs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedEndpoint, setExpandedEndpoint] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    axios.get(`${API}/haven/docs`).then(r => setDocs(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const testEndpoint = async (endpoint) => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await axios.get(`${API}/haven/divisions`);
      setTestResult({ status: res.status, data: JSON.stringify(res.data, null, 2).slice(0, 500) + "..." });
    } catch (e) {
      setTestResult({ status: e.response?.status || 500, data: JSON.stringify(e.response?.data || { error: e.message }, null, 2) });
    }
    setTesting(false);
  };

  if (loading) return <div className="flex items-center justify-center py-12"><Loader2 className="w-5 h-5 animate-spin mr-2 text-[var(--qn-foreground-muted)]" />Loading docs...</div>;

  const endpoints = docs?.endpoints || [];

  return (
    <div className="space-y-4" data-testid="haven-interactive-docs">
      {/* Base URL */}
      <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-[var(--qn-foreground-muted)] mb-1">Base URL</p>
              <code className="text-sm font-mono text-[var(--qn-primary)]">{process.env.REACT_APP_BACKEND_URL}/api/haven</code>
            </div>
            <Button variant="outline" size="sm" onClick={() => copyToClipboard(`${process.env.REACT_APP_BACKEND_URL}/api/haven`)}>
              <Copy className="w-3.5 h-3.5" />
            </Button>
          </div>
          <div className="flex items-center gap-4 mt-3 text-xs text-[var(--qn-foreground-muted)]">
            <span>Version: {docs?.version || "1.0"}</span>
            <span>Auth: Bearer Token (qng_sk_*) or Session Cookie</span>
          </div>
        </CardContent>
      </Card>

      {/* Endpoints */}
      {endpoints.map((ep, i) => {
        const methodColor = ep.method === "GET" ? "text-emerald-400 bg-emerald-500/10 border-emerald-500/20" :
          ep.method === "POST" ? "text-blue-400 bg-blue-500/10 border-blue-500/20" : "text-amber-400 bg-amber-500/10 border-amber-500/20";
        const isExpanded = expandedEndpoint === i;
        return (
          <Card key={i} className="bg-[var(--qn-card)] border-[var(--qn-border)] overflow-hidden" data-testid={`haven-endpoint-${i}`}>
            <button
              className="w-full p-4 flex items-center gap-3 text-left hover:bg-[var(--qn-surface)]/30 transition-colors"
              onClick={() => setExpandedEndpoint(isExpanded ? null : i)}
            >
              <Badge className={`${methodColor} font-mono text-[10px] px-2`}>{ep.method}</Badge>
              <code className="text-sm font-mono text-[var(--qn-foreground)] flex-1">{ep.path}</code>
              <ChevronRight className={`w-4 h-4 text-[var(--qn-foreground-muted)] transition-transform ${isExpanded ? "rotate-90" : ""}`} />
            </button>
            <AnimatePresence>
              {isExpanded && (
                <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                  <div className="px-4 pb-4 border-t border-[var(--qn-border)]">
                    <p className="text-sm text-[var(--qn-foreground-muted)] mt-3 mb-3">{ep.description}</p>
                    {ep.example_body && (
                      <div className="mb-3">
                        <p className="text-xs font-semibold text-[var(--qn-foreground-muted)] mb-1.5">Request Body</p>
                        <pre className="p-3 rounded-lg bg-[var(--qn-surface)]/50 text-xs font-mono text-[var(--qn-foreground)] overflow-x-auto">
                          {JSON.stringify(ep.example_body, null, 2)}
                        </pre>
                      </div>
                    )}
                    {ep.method === "GET" && ep.path === "/api/haven/divisions" && (
                      <div className="flex items-center gap-2">
                        <Button size="sm" onClick={() => testEndpoint(ep)} disabled={testing} className="bg-emerald-600 hover:bg-emerald-700" data-testid="haven-test-endpoint-btn">
                          {testing ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Play className="w-3.5 h-3.5 mr-1.5" />}
                          Try It
                        </Button>
                        {testResult && (
                          <Badge variant="outline" className={testResult.status < 400 ? "text-emerald-400 border-emerald-400/30" : "text-red-400 border-red-400/30"}>
                            {testResult.status}
                          </Badge>
                        )}
                      </div>
                    )}
                    {testResult && isExpanded && (
                      <pre className="mt-3 p-3 rounded-lg bg-[var(--qn-surface)]/50 text-xs font-mono text-[var(--qn-foreground)] overflow-x-auto max-h-[200px]">
                        {testResult.data}
                      </pre>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </Card>
        );
      })}

      {/* cURL Example */}
      <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <Terminal className="w-4 h-4 text-[var(--qn-primary)]" /> Quick Start
          </CardTitle>
        </CardHeader>
        <CardContent>
          <pre className="p-3 rounded-lg bg-[#0a0e1a] text-xs font-mono text-emerald-400 overflow-x-auto">
{`# List divisions
curl -H "Authorization: Bearer qng_sk_YOUR_KEY" \\
  ${process.env.REACT_APP_BACKEND_URL}/api/haven/divisions

# Execute a division
curl -X POST -H "Authorization: Bearer qng_sk_YOUR_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"division":"land","prompt":"Analyze Oakland CA for 200-unit affordable housing"}' \\
  ${process.env.REACT_APP_BACKEND_URL}/api/haven/execute`}
          </pre>
          <Button variant="outline" size="sm" className="mt-2" onClick={() => copyToClipboard(`curl -H "Authorization: Bearer qng_sk_YOUR_KEY" ${process.env.REACT_APP_BACKEND_URL}/api/haven/divisions`)}>
            <Copy className="w-3.5 h-3.5 mr-1.5" />Copy cURL
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

// ==================== Analytics Section ====================
function AnalyticsSection() {
  const [analytics, setAnalytics] = useState(null);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/haven-portal/analytics`).then(r => setAnalytics(r.data)).catch(() => {}),
      axios.get(`${API}/haven-portal/recent-activity`).then(r => setActivity(r.data.activity || [])).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex items-center justify-center py-12"><Loader2 className="w-5 h-5 animate-spin mr-2 text-[var(--qn-foreground-muted)]" />Loading analytics...</div>;

  const stats = [
    { label: "Total API Calls", value: analytics?.total_api_calls || 0, icon: Activity },
    { label: "Last 30 Days", value: analytics?.calls_30d || 0, icon: BarChart3 },
    { label: "Today", value: analytics?.calls_today || 0, icon: Clock },
    { label: "HAVEN Executions", value: analytics?.total_executions || 0, icon: Zap },
  ];

  return (
    <div className="space-y-4" data-testid="haven-analytics-section">
      {/* Stat Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {stats.map((s, i) => (
          <Card key={i} className="bg-[var(--qn-card)] border-[var(--qn-border)]">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <s.icon className="w-4 h-4 text-[var(--qn-primary)]" />
                <span className="text-xs text-[var(--qn-foreground-muted)]">{s.label}</span>
              </div>
              <span className="text-2xl font-mono font-bold text-[var(--qn-foreground)]">{s.value.toLocaleString()}</span>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Rate Limits */}
      {analytics?.rate_limits && (
        <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Shield className="w-4 h-4 text-amber-500" /> Rate Limits
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4">
              <div className="p-3 rounded-lg bg-[var(--qn-surface)]/30">
                <p className="text-xs text-[var(--qn-foreground-muted)]">Requests/min</p>
                <p className="text-lg font-mono font-bold text-[var(--qn-foreground)]">{analytics.rate_limits.requests_per_minute}</p>
              </div>
              <div className="p-3 rounded-lg bg-[var(--qn-surface)]/30">
                <p className="text-xs text-[var(--qn-foreground-muted)]">Concurrent Executions</p>
                <p className="text-lg font-mono font-bold text-[var(--qn-foreground)]">{analytics.rate_limits.concurrent_executions}</p>
              </div>
              <div className="p-3 rounded-lg bg-[var(--qn-surface)]/30">
                <p className="text-xs text-[var(--qn-foreground-muted)]">Daily Limit</p>
                <p className="text-lg font-mono font-bold text-[var(--qn-foreground)]">
                  {analytics.rate_limits.used_today}/{analytics.rate_limits.daily_limit}
                </p>
                <div className="w-full h-1.5 rounded-full bg-[var(--qn-border)] mt-2 overflow-hidden">
                  <div className="h-full rounded-full bg-[var(--qn-primary)]" style={{ width: `${Math.min(100, (analytics.rate_limits.used_today / analytics.rate_limits.daily_limit) * 100)}%` }} />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Endpoint Breakdown */}
      {analytics?.endpoint_breakdown?.length > 0 && (
        <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold">Endpoint Usage</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {analytics.endpoint_breakdown.map((ep, i) => (
                <div key={i} className="flex items-center justify-between py-2 border-b border-[var(--qn-border)]/50 last:border-0">
                  <code className="text-xs font-mono text-[var(--qn-foreground)]">{ep.endpoint}</code>
                  <span className="text-xs font-mono text-[var(--qn-primary)]">{ep.count.toLocaleString()} calls</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent Activity */}
      <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold">Recent Activity</CardTitle>
        </CardHeader>
        <CardContent>
          {activity.length === 0 ? (
            <p className="text-sm text-[var(--qn-foreground-muted)] py-4 text-center">No API activity yet. Make your first API call to see activity here.</p>
          ) : (
            <div className="space-y-1.5 max-h-[250px] overflow-y-auto">
              {activity.map((a, i) => (
                <div key={i} className="flex items-center gap-3 py-1.5 px-2 rounded text-xs hover:bg-[var(--qn-surface)]/30">
                  <Badge variant="outline" className="text-[9px] font-mono">{a.method}</Badge>
                  <code className="text-[var(--qn-foreground)] flex-1 font-mono">{a.endpoint}</code>
                  <span className="text-[var(--qn-foreground-muted)]">{a.auth_method}</span>
                  <span className="text-[var(--qn-foreground-subtle)]">{new Date(a.timestamp).toLocaleString()}</span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ==================== Embed Widget Section ====================
function EmbedWidgetSection() {
  const embedSnippet = `<iframe
  src="${process.env.REACT_APP_BACKEND_URL}/api/haven/widget?division=land"
  width="100%"
  height="600"
  frameborder="0"
  style="border-radius: 12px; border: 1px solid #1a2333;"
></iframe>`;

  const jsSnippet = `<script>
  const HAVEN_API = "${process.env.REACT_APP_BACKEND_URL}/api/haven";
  const API_KEY = "qng_sk_YOUR_KEY";

  async function executeDivision(division, prompt) {
    const res = await fetch(\`\${HAVEN_API}/execute\`, {
      method: "POST",
      headers: {
        "Authorization": \`Bearer \${API_KEY}\`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ division, prompt }),
    });
    return res.json();
  }

  // Example: Execute Land Intelligence
  executeDivision("land", "Analyze Oakland CA for affordable housing")
    .then(data => console.log("Execution started:", data));
</script>`;

  return (
    <div className="space-y-4" data-testid="haven-embed-section">
      <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <Globe className="w-4 h-4 text-[var(--qn-primary)]" />
            Embed HAVEN Widget
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-[var(--qn-foreground-muted)]">
            Embed HAVEN into any webpage with a simple iframe or JavaScript snippet.
          </p>
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-[var(--qn-foreground-muted)]">iframe Embed</p>
              <Button variant="outline" size="sm" className="h-6 text-[10px]" onClick={() => copyToClipboard(embedSnippet)}>
                <Copy className="w-3 h-3 mr-1" />Copy
              </Button>
            </div>
            <pre className="p-3 rounded-lg bg-[#0a0e1a] text-xs font-mono text-cyan-400 overflow-x-auto">{embedSnippet}</pre>
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-[var(--qn-foreground-muted)]">JavaScript SDK</p>
              <Button variant="outline" size="sm" className="h-6 text-[10px]" onClick={() => copyToClipboard(jsSnippet)}>
                <Copy className="w-3 h-3 mr-1" />Copy
              </Button>
            </div>
            <pre className="p-3 rounded-lg bg-[#0a0e1a] text-xs font-mono text-emerald-400 overflow-x-auto">{jsSnippet}</pre>
          </div>
        </CardContent>
      </Card>

      {/* Available Divisions */}
      <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold">Available Division IDs</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
            {["land", "regulatory", "design", "supply", "construction", "financial", "residents", "operations", "policy", "command"].map(d => (
              <div key={d} className="p-2 rounded-lg bg-emerald-500/5 border border-emerald-500/20 text-center">
                <code className="text-xs font-mono text-emerald-400">{d}</code>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ==================== Webhooks Section ====================
function WebhooksSection() {
  const [webhooks, setWebhooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newUrl, setNewUrl] = useState("");
  const [creating, setCreating] = useState(false);

  const fetchWebhooks = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/webhooks`);
      setWebhooks(res.data || []);
    } catch { /* non-blocking */ }
    setLoading(false);
  }, []);

  useEffect(() => { fetchWebhooks(); }, [fetchWebhooks]);

  const createWebhook = async () => {
    if (!newUrl.trim()) { toast.error("Enter a webhook URL"); return; }
    setCreating(true);
    try {
      await axios.post(`${API}/webhooks`, { url: newUrl.trim(), events: ["haven.execution.completed", "haven.execution.failed"] });
      toast.success("Webhook created");
      setNewUrl("");
      fetchWebhooks();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create webhook");
    }
    setCreating(false);
  };

  const deleteWebhook = async (id) => {
    try {
      await axios.delete(`${API}/webhooks/${id}`);
      toast.success("Webhook deleted");
      fetchWebhooks();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const toggleWebhook = async (id) => {
    try {
      await axios.put(`${API}/webhooks/${id}/toggle`);
      fetchWebhooks();
    } catch { /* non-blocking */ }
  };

  const testWebhook = async (id) => {
    try {
      const res = await axios.post(`${API}/webhooks/${id}/test`);
      if (res.data.success) toast.success(`Test ping sent (${res.data.status_code})`);
      else toast.error(`Test failed: ${res.data.error}`);
    } catch { toast.error("Test failed"); }
  };

  return (
    <div className="space-y-4" data-testid="haven-webhooks-section">
      <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <Webhook className="w-4 h-4 text-[var(--qn-primary)]" />
            Create Webhook
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-3">
            <Input
              placeholder="https://your-server.com/webhook/haven"
              value={newUrl}
              onChange={(e) => setNewUrl(e.target.value)}
              className="bg-[var(--qn-surface)] border-[var(--qn-border)] flex-1"
              data-testid="haven-webhook-url-input"
            />
            <Button onClick={createWebhook} disabled={creating} className="bg-[var(--qn-primary)]" data-testid="haven-create-webhook-btn">
              {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-2" />Add</>}
            </Button>
          </div>
          <p className="text-xs text-[var(--qn-foreground-muted)] mt-2">Events: haven.execution.completed, haven.execution.failed</p>
        </CardContent>
      </Card>

      <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold">Active Webhooks</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-6"><Loader2 className="w-5 h-5 animate-spin mr-2 text-[var(--qn-foreground-muted)]" /></div>
          ) : webhooks.length === 0 ? (
            <p className="text-sm text-[var(--qn-foreground-muted)] py-4 text-center">No webhooks configured.</p>
          ) : (
            <div className="space-y-2">
              {webhooks.map((wh) => (
                <div key={wh.webhook_id} className="flex items-center justify-between p-3 rounded-lg bg-[var(--qn-surface)]/30 border border-[var(--qn-border)]" data-testid={`haven-webhook-${wh.webhook_id}`}>
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className={`w-2 h-2 rounded-full shrink-0 ${wh.active ? "bg-emerald-500" : "bg-[var(--qn-foreground-subtle)]"}`} />
                    <div className="min-w-0">
                      <p className="text-sm font-mono text-[var(--qn-foreground)] truncate">{wh.url}</p>
                      <p className="text-[10px] text-[var(--qn-foreground-muted)]">{(wh.events || []).join(", ")}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0 ml-3">
                    <Button variant="ghost" size="sm" onClick={() => testWebhook(wh.webhook_id)} className="h-7 text-xs">
                      <Play className="w-3 h-3" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => toggleWebhook(wh.webhook_id)} className="h-7 text-xs">
                      {wh.active ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => deleteWebhook(wh.webhook_id)} className="h-7 text-xs text-red-400 hover:bg-red-500/10">
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ==================== Execution Playground Section ====================
const DIVISIONS = [
  { value: "land", label: "Land Intelligence" },
  { value: "regulatory", label: "Regulatory Compliance" },
  { value: "design", label: "Architecture & Design" },
  { value: "supply", label: "Supply Chain" },
  { value: "construction", label: "Construction Scheduling" },
  { value: "financial", label: "Financial Engineering" },
  { value: "residents", label: "Resident Services" },
  { value: "operations", label: "Property Operations" },
  { value: "policy", label: "Policy & Advocacy" },
  { value: "command", label: "Platform Command" },
];

const SAMPLE_PROMPTS = [
  "Analyze Oakland, CA for a 200-unit affordable housing project with LIHTC financing",
  "Evaluate San Francisco Mission District for multi-family transit-oriented development",
  "Design a net-zero energy 150-unit senior housing complex with universal accessibility",
  "Source sustainable building materials for a 300-unit project under $180/sqft",
];

function ExecutionPlayground() {
  const [division, setDivision] = useState("land");
  const [prompt, setPrompt] = useState("");
  const [executing, setExecuting] = useState(false);
  const [executionId, setExecutionId] = useState(null);
  const [events, setEvents] = useState([]);
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState(null);
  const eventSourceRef = useRef(null);
  const eventsEndRef = useRef(null);

  // Auto-scroll events feed
  useEffect(() => {
    eventsEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [events]);

  // Cleanup SSE on unmount
  useEffect(() => {
    return () => { eventSourceRef.current?.close(); };
  }, []);

  const startExecution = async () => {
    if (!prompt.trim()) { toast.error("Enter a prompt to execute"); return; }
    setExecuting(true);
    setEvents([]);
    setSummary(null);
    setError(null);
    setExecutionId(null);

    try {
      const res = await axios.post(`${API}/haven/execute`, { division, prompt: prompt.trim() });
      const execId = res.data.execution_id;
      setExecutionId(execId);
      setEvents(prev => [...prev, { type: "system", text: `Execution started: ${res.data.swarm_name}`, detail: `${res.data.agent_count} agents | ID: ${execId}`, ts: new Date() }]);

      // Connect SSE stream
      const es = new EventSource(`${API}/haven/executions/${execId}/stream`);
      eventSourceRef.current = es;

      es.onmessage = (evt) => {
        try {
          const data = JSON.parse(evt.data);
          if (data.type === "complete" || data.status === "completed") {
            setEvents(prev => [...prev, { type: "complete", text: "Execution complete", detail: `${data.total_tokens?.toLocaleString() || "?"} tokens | $${Number(data.total_cost || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, ts: new Date() }]);
            setSummary(data);
            es.close();
            setExecuting(false);
            // Fetch full result
            axios.get(`${API}/haven/executions/${execId}`).then(r => setSummary(r.data)).catch(() => {});
          } else if (data.type === "agent_complete" || data.agent_id) {
            setEvents(prev => [...prev, {
              type: "agent",
              text: data.agent_name || data.agent_id || "Agent",
              detail: data.response_text?.slice(0, 120) || data.model || "",
              model: data.model,
              tokens: data.tokens,
              mediaUrl: data.media_url,
              ts: new Date(),
            }]);
          } else if (data.type === "error") {
            setEvents(prev => [...prev, { type: "error", text: "Error", detail: data.message || data.error || JSON.stringify(data), ts: new Date() }]);
          } else {
            setEvents(prev => [...prev, { type: "info", text: data.type || "Event", detail: JSON.stringify(data).slice(0, 150), ts: new Date() }]);
          }
        } catch {
          setEvents(prev => [...prev, { type: "raw", text: evt.data?.slice(0, 200), ts: new Date() }]);
        }
      };

      es.onerror = () => {
        es.close();
        if (executing) {
          setEvents(prev => [...prev, { type: "system", text: "Stream ended", ts: new Date() }]);
          setExecuting(false);
          // Fetch final result
          axios.get(`${API}/haven/executions/${execId}`).then(r => setSummary(r.data)).catch(() => {});
        }
      };
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
      setExecuting(false);
    }
  };

  const stopExecution = () => {
    eventSourceRef.current?.close();
    setExecuting(false);
    setEvents(prev => [...prev, { type: "system", text: "Execution stopped by user", ts: new Date() }]);
  };

  const completedAgents = events.filter(e => e.type === "agent").length;

  return (
    <div className="space-y-4" data-testid="haven-playground-section">
      {/* Config */}
      <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <Rocket className="w-4 h-4 text-[var(--qn-primary)]" />
            Execute HAVEN Division
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-3">
            <Select value={division} onValueChange={setDivision} disabled={executing}>
              <SelectTrigger className="w-52 bg-[var(--qn-surface)] border-[var(--qn-border)]" data-testid="playground-division-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DIVISIONS.map(d => (
                  <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex-1">
              <PromptInput
                value={prompt}
                onChange={setPrompt}
                onSubmit={startExecution}
                placeholder="Ask anything — describe the housing project to analyze"
                disabled={executing}
                loading={executing}
                maxLength={800}
                submitLabel="Execute Division"
              />
            </div>
          </div>

          {/* Sample Prompts */}
          {!executing && !events.length && (
            <div className="flex flex-wrap gap-2">
              {SAMPLE_PROMPTS.map((sp, i) => (
                <button
                  key={i}
                  onClick={() => setPrompt(sp)}
                  className="px-3 py-1.5 rounded-full text-[11px] bg-[var(--qn-surface)]/50 border border-[var(--qn-border)] text-[var(--qn-foreground-muted)] hover:border-[var(--qn-primary)]/40 hover:text-[var(--qn-foreground)] transition-all"
                  data-testid={`playground-sample-${i}`}
                >
                  {sp.slice(0, 60)}...
                </button>
              ))}
            </div>
          )}

          <div className="flex gap-3">
            {executing && (
              <Button
                onClick={stopExecution}
                className="bg-amber-600 hover:bg-amber-700"
                data-testid="playground-stop-btn"
              >
                <StopCircle className="w-4 h-4 mr-2" />Stop
              </Button>
            )}
            {events.length > 0 && !executing && (
              <Button
                variant="outline"
                onClick={() => { setEvents([]); setSummary(null); setError(null); setExecutionId(null); }}
                data-testid="playground-clear-btn"
              >
                Clear
              </Button>
            )}
          </div>

          {error && (
            <div className="p-3 rounded-lg border border-red-500/30 bg-red-500/5 text-sm text-red-400 flex items-start gap-2" data-testid="playground-error">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />{error}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Live Execution Feed */}
      {events.length > 0 && (
        <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]">
          <CardHeader className="pb-3 flex flex-row items-center justify-between">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              {executing ? <Loader2 className="w-4 h-4 animate-spin text-amber-500" /> : <CheckCircle className="w-4 h-4 text-emerald-500" />}
              Live Execution Feed
              {executing && <span className="text-xs font-mono text-amber-400 ml-2">{completedAgents} agents complete</span>}
            </CardTitle>
            {executionId && (
              <code className="text-[10px] font-mono text-[var(--qn-foreground-muted)]">{executionId}</code>
            )}
          </CardHeader>
          <CardContent className="p-0">
            <div className="max-h-[350px] overflow-y-auto">
              {events.map((evt, i) => {
                const dotColor = evt.type === "agent" ? "bg-emerald-500" :
                  evt.type === "complete" ? "bg-blue-500" :
                  evt.type === "error" ? "bg-red-500" :
                  evt.type === "system" ? "bg-amber-500" : "bg-[var(--qn-foreground-muted)]";
                return (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: -8 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.15 }}
                    className="flex gap-3 px-4 py-2.5 border-b border-[var(--qn-border)]/20 hover:bg-[var(--qn-surface)]/20 transition-colors"
                    data-testid={`playground-event-${i}`}
                  >
                    <div className={`w-2 h-2 rounded-full shrink-0 mt-1.5 ${dotColor}`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="text-xs font-medium text-[var(--qn-foreground)]">{evt.text}</span>
                        {evt.model && <Badge variant="outline" className="text-[9px]">{evt.model}</Badge>}
                        {evt.tokens && <span className="text-[10px] text-[var(--qn-foreground-subtle)]">{evt.tokens} tok</span>}
                      </div>
                      {evt.detail && <p className="text-[11px] text-[var(--qn-foreground-muted)] line-clamp-2">{evt.detail}</p>}
                      {evt.mediaUrl && (
                        <a href={evt.mediaUrl} target="_blank" rel="noopener noreferrer" className="text-[10px] text-[var(--qn-primary)] hover:underline flex items-center gap-1 mt-1">
                          <ExternalLink className="w-3 h-3" />Media
                        </a>
                      )}
                    </div>
                    <span className="text-[9px] text-[var(--qn-foreground-subtle)] shrink-0 mt-0.5">{evt.ts.toLocaleTimeString()}</span>
                  </motion.div>
                );
              })}
              <div ref={eventsEndRef} />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Execution Summary */}
      {summary && !executing && (
        <Card className="bg-[var(--qn-card)] border-emerald-500/30">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-emerald-500" />
              Execution Summary
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
              {[
                { label: "Status", value: summary.status || "completed", icon: Activity },
                { label: "Agents", value: summary.agent_count || completedAgents, icon: Users },
                { label: "Tokens", value: (summary.total_tokens || 0).toLocaleString(), icon: Hash },
                { label: "Cost", value: `$${Number(summary.total_cost || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, icon: Zap },
              ].map((s, i) => (
                <div key={i} className="p-3 rounded-lg bg-[var(--qn-surface)]/30 border border-[var(--qn-border)]">
                  <div className="flex items-center gap-1.5 mb-1">
                    <s.icon className="w-3.5 h-3.5 text-[var(--qn-primary)]" />
                    <span className="text-[10px] text-[var(--qn-foreground-muted)]">{s.label}</span>
                  </div>
                  <span className="text-sm font-mono font-bold text-[var(--qn-foreground)]">{s.value}</span>
                </div>
              ))}
            </div>

            {/* Division Summary */}
            {summary.division_summary && Object.keys(summary.division_summary).length > 0 && (
              <div>
                <p className="text-xs font-semibold text-[var(--qn-foreground-muted)] mb-2">Division Breakdown</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {Object.entries(summary.division_summary).map(([key, val]) => (
                    <div key={key} className="p-2 rounded-lg bg-emerald-500/5 border border-emerald-500/15 text-xs">
                      <span className="font-medium text-emerald-400 capitalize">{key}</span>
                      <span className="text-[var(--qn-foreground-muted)] ml-2">{val.agents || val.count || "?"} agents</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* cURL equivalent */}
            <div className="mt-4">
              <p className="text-[10px] text-[var(--qn-foreground-muted)] mb-1">Equivalent API call:</p>
              <pre className="p-2 rounded bg-[#0a0e1a] text-[10px] font-mono text-emerald-400 overflow-x-auto">
{`curl -X POST -H "Authorization: Bearer qng_sk_YOUR_KEY" \\
  -H "Content-Type: application/json" \\
  -d '${JSON.stringify({ division, prompt: prompt.slice(0, 60) + "..." })}' \\
  ${process.env.REACT_APP_BACKEND_URL}/api/haven/execute`}
              </pre>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ==================== Main HAVEN Developer Portal ====================
export default function HavenDeveloperPortal() {
  const [activeTab, setActiveTab] = useState("playground");

  return (
    <div className="space-y-6" data-testid="haven-developer-portal">
      {/* Header */}
      <div>
        <div className="flex items-center gap-3 mb-1">
          <h1 className="text-2xl font-outfit font-bold text-[var(--qn-foreground)]">HAVEN Developer Portal</h1>
          <Badge className="bg-emerald-600/20 text-emerald-400 border-emerald-500/30 text-xs">API v1.0</Badge>
        </div>
        <p className="text-sm text-[var(--qn-foreground-muted)]">
          Manage API keys, explore endpoints, monitor usage, and integrate HAVEN into your platform.
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-[var(--qn-surface)] border border-[var(--qn-border)]">
          <TabsTrigger value="playground" data-testid="haven-portal-tab-playground"><Rocket className="w-4 h-4 mr-2" />Playground</TabsTrigger>
          <TabsTrigger value="keys" data-testid="haven-portal-tab-keys"><Key className="w-4 h-4 mr-2" />API Keys</TabsTrigger>
          <TabsTrigger value="docs" data-testid="haven-portal-tab-docs"><BookOpen className="w-4 h-4 mr-2" />Docs</TabsTrigger>
          <TabsTrigger value="analytics" data-testid="haven-portal-tab-analytics"><BarChart3 className="w-4 h-4 mr-2" />Analytics</TabsTrigger>
          <TabsTrigger value="embed" data-testid="haven-portal-tab-embed"><Code2 className="w-4 h-4 mr-2" />Embed</TabsTrigger>
          <TabsTrigger value="webhooks" data-testid="haven-portal-tab-webhooks"><Webhook className="w-4 h-4 mr-2" />Webhooks</TabsTrigger>
        </TabsList>

        <TabsContent value="playground"><ExecutionPlayground /></TabsContent>
        <TabsContent value="keys"><APIKeysSection /></TabsContent>
        <TabsContent value="docs"><InteractiveDocsSection /></TabsContent>
        <TabsContent value="analytics"><AnalyticsSection /></TabsContent>
        <TabsContent value="embed"><EmbedWidgetSection /></TabsContent>
        <TabsContent value="webhooks"><WebhooksSection /></TabsContent>
      </Tabs>
    </div>
  );
}
